/**
 * Frontend utility formatters for FEX Logistics
 */

/**
 * Format a raw tracking code to always have the FX prefix.
 */
export function formatTrackingCode(code) {
  if (!code) return '—';
  const c = String(code).toUpperCase().trim();
  return c.startsWith('FX') ? c : `FX${c}`;
}

/**
 * Infer shipment service type from tracking code digit.
 */
export function inferTypeFromCode(code) {
  const c = String(code || '').toUpperCase();
  const idx = c.startsWith('FX') ? 2 : 0;
  const lead = c[idx];
  if (lead === '1') return 'local';
  if (lead === '2') return 'interstate';
  if (lead === '3') return 'international';
  return 'interstate';
}

// ─── New unified international / all-shipment statuses ──────────────────────
const ALL_STATUSES = [
  'Shipment registered',
  'Shipment booked',
  'Shipment transfer to third-party',
  'Shipment on hold',
  'Rider picked',
  'Out for delivery',
  'Shipment in transit',
  'Shipment departed',
  'Shipment arrival sorting facility',
  'Shipment arrived service center',
  'Return to sender',
  'Shipment Rescheduled',
  'Shipment available for pickup',
  'Int\'l Shipment undergoing custom clearance',
  'Int\'l Shipment arrival sorting warehouse',
  'Int\'l Shipment arrival sorting warehouse',
  'Delivered',
  'Delayed',
  'Lost',
  'Cancelled'
];

const INTERNATIONAL_STATUSES = ALL_STATUSES;
const LOCAL_STATUSES = ALL_STATUSES;
const INTERSTATE_STATUSES = ALL_STATUSES;

/**
 * Returns the list of valid statuses an agent can set for a given service type.
 * International shipments now use the new extended status set.
 */
export function getAgentTrackingStatuses(serviceType) {
  const t = String(serviceType || '').toLowerCase();
  if (t === 'international') return INTERNATIONAL_STATUSES;
  if (t === 'interstate' || t === 'haulage') return INTERSTATE_STATUSES;
  if (t === 'local' || t === 'express') return LOCAL_STATUSES;
  // Default: return combined set so agents can pick any status
  return INTERSTATE_STATUSES;
}

/**
 * Returns the ordered status list shown to customers on the tracking page.
 * International shipments show the full journey.
 */
export function getCustomerTrackingStatuses(serviceType) {
  return getAgentTrackingStatuses(serviceType);
}

/**
 * Map a raw DB status to a normalised colour class for badges.
 */
export function getStatusColor(status) {
  const s = (status || '').toLowerCase();
  
  if (s.includes('delivered')) return 'bg-green-100 text-green-800';
  if (s.includes('delayed')) return 'bg-orange-100 text-orange-800';
  if (s.includes('lost') || s.includes('cancelled')) return 'bg-red-100 text-red-800';
  if (s.includes('on hold')) return 'bg-gray-100 text-gray-800';
  if (s.includes('return to sender')) return 'bg-red-100 text-red-800';
  
  if (s.includes('registered') || s.includes('booked')) return 'bg-yellow-100 text-yellow-800';
  
  if (s.includes('transfer') || s.includes('transit') || s.includes('departed')) 
    return 'bg-indigo-100 text-indigo-800';
    
  if (s.includes('rider picked') || s.includes('out for delivery')) 
    return 'bg-blue-100 text-blue-800';
    
  if (s.includes('arrival sorting facility') || s.includes('arrived service center')) 
    return 'bg-yellow-100 text-yellow-800';
    
  if (s.includes('rescheduled')) return 'bg-orange-100 text-orange-800';
  if (s.includes('available for pickup')) return 'bg-purple-100 text-purple-800';
  
  if (s.includes('int\'l shipment')) return 'bg-teal-100 text-teal-800';
  
  return 'bg-purple-100 text-purple-800';
}

/**
 * Format a price number to a Naira string.
 * @param {number} amount
 */
export function formatCurrency(amount) {
  return `₦${Number(amount || 0).toLocaleString()}`;
}

/**
 * Format a date string or Date object to a short locale string.
 */
export function formatDate(date) {
  if (!date) return '—';
  try {
    return new Date(date).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return String(date);
  }
}

/**
 * Format a date + time.
 */
export function formatDateTime(date) {
  if (!date) return '—';
  try {
    return new Date(date).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return String(date);
  }
}